import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import { usePesTestQuery } from "generated/hooks";
import { useFetchAllResults } from "~/hooks/useFetchAllResults";
import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { Skeleton } from "~/components/ui/skeleton";
import { IndicatorCard } from "~/components/dashboard/single-result/IndicatorCard";
import {
  EXCLUDED_INDICATORS,
  ORDER,
  RESTRICTED_INDICATORS,
} from "~/components/dashboard/single-result/constants";
import { AlertCircle, HelpCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Separator } from "~/components/ui/separator";
import { titleCase } from "~/util/string.util";

const SingleResult: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const stickyRef = useRef<HTMLDivElement>(null);
  const { data, loading, error, fetchMore, startPolling, stopPolling } =
    usePesTestQuery({
      variables: { id: id! },
      skip: !id,
      pollInterval: 5000,
    });
  useFetchAllResults(fetchMore, data?.pesTest?.results);

  // `hasResults` ignores the viewer's customer indicator limit, so only keep
  // polling while the test genuinely has nothing processed yet.
  const hasResults = data?.pesTest?.hasResults ?? false;
  useEffect(() => {
    if (hasResults) {
      stopPolling();
    } else if (id) {
      startPolling(5000);
    }
  }, [hasResults, id, startPolling, stopPolling]);

  // Debug sticky positioning - runs after data loads
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!stickyRef.current) {
        console.log("=== STICKY DEBUG: ref not found ===");
        return;
      }

      const el = stickyRef.current;
      const computedStyle = window.getComputedStyle(el);

      console.log("=== STICKY DEBUG ===");
      console.log("Element:", el);
      console.log("position:", computedStyle.position);
      console.log("top:", computedStyle.top);
      console.log("Element height:", el.offsetHeight);

      let ancestor: HTMLElement | null = el.parentElement;
      let level = 1;
      while (ancestor && level <= 10) {
        const style = window.getComputedStyle(ancestor);
        const hasOverflow = style.overflow !== "visible" || style.overflowY !== "visible";
        console.log(`Ancestor ${level}:`, {
          tagName: ancestor.tagName,
          className: ancestor.className.slice(0, 100),
          overflow: style.overflow,
          overflowY: style.overflowY,
          height: ancestor.offsetHeight,
          scrollHeight: ancestor.scrollHeight,
          position: style.position,
          hasOverflow,
        });
        ancestor = ancestor.parentElement;
        level++;
      }
      console.log("=== END DEBUG ===");
    }, 500);

    return () => clearTimeout(timer);
  }, [data]);

  const filteredResults =
    data?.pesTest?.results?.nodes
      ?.filter((result) => {
        const name = result.indicator?.indicatorDefinition?.name;
        if (!name) {
          return false;
        }
        if (RESTRICTED_INDICATORS.includes(name.toLowerCase())) {
          return false;
        }
        return !EXCLUDED_INDICATORS.includes(name.toLowerCase());
      })
      .filter(
        (result) =>
          result.indicator?.model?.name === data?.pesTest?.originalModelName
      )
      .sort((a, b) => {
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      })
      .filter((result, index, self) => {
        return (
          self.findIndex(
            (r) =>
              r.indicator?.indicatorDefinition?.name ===
              result.indicator?.indicatorDefinition?.name
          ) === index
        );
      })
      .sort((a, b) => {
        return (
          ORDER.indexOf((a.indicator?.indicatorDefinition?.name ?? "").toLowerCase()) -
          ORDER.indexOf((b.indicator?.indicatorDefinition?.name ?? "").toLowerCase())
        );
      }) ?? [];

  const pesTest = data?.pesTest;
  const lastAnalysedAt = pesTest?.results?.nodes?.reduce<Date | null>(
    (latest, result) => {
      const resultUpdatedAt = new Date(result.updatedAt);
      return !latest || resultUpdatedAt > latest ? resultUpdatedAt : latest;
    },
    null
  );

  if (loading) {
    return (
      <PageTemplate title="Test Result" activeItem="My Tests">
        <div className="space-y-4">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-4 w-1/4" />
          <div className="grid gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        </div>
      </PageTemplate>
    );
  }

  if (error || !pesTest) {
    return (
      <PageTemplate title="Test Result" activeItem="My Tests">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error ? error.message : "Test not found."}
          </AlertDescription>
        </Alert>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      title={pesTest.name}
      activeItem="My Tests"
      breadcrumbs={[
        { label: "My Tests", path: "/dashboard" },
        { label: pesTest.name, path: `/test/${id}` },
      ]}
    >
      <div className="grid gap-6 md:grid-cols-[300px_minmax(0,1fr)] md:items-start">
        <div ref={stickyRef} className="md:sticky md:top-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Test Details</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 text-sm">
              <div className="grid gap-1">
                <span className="text-muted-foreground">Test created at</span>
                <span className="font-medium">
                  {format(new Date(pesTest.createdAt), "dd/MM/yyyy")}
                </span>
              </div>
              <Separator />
              <div className="grid gap-1">
                <span className="text-muted-foreground">Last analysed</span>
                <span className="font-medium">
                  {lastAnalysedAt
                    ? format(lastAnalysedAt, "d MMM yyyy, HH:mm")
                    : "Not yet analysed"}
                </span>
              </div>
              <Separator />
              <div className="grid gap-1">
                <span className="text-muted-foreground">Zone Type</span>
                <span className="font-medium">
                  {pesTest.isReferenceZone ? "Reference zone" : "In-field"}
                </span>
              </div>
              <Separator />
              <div className="grid gap-1">
                <span className="text-muted-foreground">Season</span>
                <span className="font-medium">{titleCase(pesTest.season)}</span>
              </div>
              <Separator />
              <div className="grid gap-1">
                <span className="text-muted-foreground">Moisture</span>
                <span className="font-medium">{titleCase(pesTest.moisture)}</span>
              </div>
              <Separator />
              <div className="grid gap-1">
                <span className="text-muted-foreground">Crop Type</span>
                <span className="font-medium">
                  {pesTest.displayCropTypeName || "N/A"}
                </span>
              </div>
              <Separator />
              <div className="grid gap-1">
                <span className="text-muted-foreground">Farm</span>
                <span className="font-medium">{pesTest.farm?.name || "N/A"}</span>
              </div>
              <Separator />
              <div className="grid gap-1">
                <span className="text-muted-foreground">Field</span>
                <span className="font-medium">{pesTest.field?.name || "N/A"}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col gap-4 min-w-0">
          {filteredResults.map((result, index) => (
            <IndicatorCard
              key={index}
              value={result.value}
              minValue={result.indicator?.minValue ?? 0}
              maxValue={result.indicator?.maxValue ?? 0}
              decimalPlaces={
                (result.indicator?.indicatorDefinition as any)
                  .displayDecimalPlaces ?? 2
              }
              indicatorDescription={
                result.indicator?.indicatorDefinition?.description ?? ""
              }
              indicatorName={result.indicator?.indicatorDefinition?.name ?? ""}
              indicatorDisplayName={
                result.indicator?.indicatorDefinition?.displayName ?? undefined
              }
              indicatorUnit={result.indicator?.indicatorDefinition?.unit ?? ""}
              reversed={
                result.indicator?.indicatorDefinition?.name ===
                "Metabolic quotient"
              }
            />
          ))}

          {(pesTest.results?.nodes?.length ?? 0) === 0 && (
            <Card className="flex flex-col items-center justify-center text-center gap-4 py-10">
              <p className="text-base font-medium text-muted-foreground">
                No results available.
              </p>
              <div className="w-3/4 border-t" />
              <div className="flex flex-row items-center gap-2 px-6">
                <HelpCircle className="w-5 h-5 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {hasResults
                    ? "This test has been processed, but none of its " +
                      "indicators are available under your account's " +
                      "indicator settings. Please contact PES Technologies " +
                      "if you believe this is an error."
                    : "This is likely because this test is awaiting " +
                      "processing; most tests are processed within 30 " +
                      "minutes. This page will periodically check for new " +
                      "results."}
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </PageTemplate>
  );
};

export default SingleResult;
